import { EmeraldButton } from "@emerald-react/button";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { useAppSelector } from "@app/hooks";
import { selectVisiblePOICount } from "@features/survey/poisSelectors";
import {
  selectIsPolygonDrawingMode,
  selectPolygonCount,
} from "@features/survey/polygonSelectors";
import { setPolygonDrawingMode } from "@features/survey/surveySlice";
import useSurveyState from "@hooks/useSurveyState";
import { useUpdateSurveyFlagsMutation } from "@services/survey-core";
import Analytics from "@shared/utils/tagManagerAnalytics";
import { setOpenPropertiesDrawer } from "@survey/surveySlice";
import { isUSUser } from "@utils/countryUtils";
import React, { useState } from "react";
import LegendDrawer from "./LegendDrawer";
import MarketMapKey from "./MarketMapKey";

const MarketMapSection = () => {
  const { survey, dispatch } = useSurveyState();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const [isLegendDrawerOpen, setIsLegendDrawerOpen] = useState(false);
  const [isMapKeyDrawerOpen, setIsMapKeyDrawerOpen] = useState(false);

  // Get count of visible POIs to control checkbox enable/disable
  const visiblePOICount = useAppSelector(selectVisiblePOICount);
  const hasVisiblePOIs = visiblePOICount > 0;

  // Polygon drawing state
  const isPolygonDrawingMode = useAppSelector(selectIsPolygonDrawingMode);
  const polygonCount = useAppSelector(selectPolygonCount);

  const handleCheckboxChange = (name: string) => (checked: boolean) => {
    if (survey) {
      updateSurveyFlags({ id: survey.id, [name]: checked });
    }
  };

  const handleCheckboxChangeForCompsAndKeys =
    (name: string) => (checked: boolean) => {
      if (survey) {
        const currentMapBamData = survey.marketMapBamData
          ? JSON.parse(survey.marketMapBamData)
          : {};
        const updatedMapBamData = JSON.stringify({
          ...currentMapBamData,
          mapReset: true,
        });
        let event = "Toggle Show Lease Comps";
        switch (name) {
          case "isIncludeMapKey":
            event = "Toggle Show Map Key";
            break;
          case "isIncludePOI":
            event = "Toggle Show POI";
            break;
          default:
            event = "Toggle Show Lease Comps";
            break;
        }
        Analytics.send({
          event: event,
          SurveyID: survey.id,
          SurveyName: survey.name,
          ToggleShowMapKeyValue: checked,
        });
        updateSurveyFlags({
          id: survey.id,
          [name]: checked,
          marketMapBamData: updatedMapBamData,
        });
      }
    };

  const toggleOpenLegendDrawer = (e?: React.MouseEvent) => {
    e?.preventDefault();
    const newState = !isLegendDrawerOpen;
    setIsLegendDrawerOpen(newState);

    // Scroll to legend section when drawer opens
    if (newState) {
      setTimeout(() => {
        const legendElement = document.getElementById("legend-section");
        if (legendElement) {
          legendElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  const toggleOpenMapKeyDrawer = (e?: React.MouseEvent) => {
    e?.preventDefault();
    const newState = !isMapKeyDrawerOpen;
    Analytics.send({
      event: "Edit Map Key",
      SurveyID: survey?.id,
      SurveyName: survey?.name,
    });
    setIsMapKeyDrawerOpen(newState);

    // Scroll to map key section when drawer opens
    if (newState) {
      setTimeout(() => {
        const mapKeyElement = document.getElementById("map-key-section");
        if (mapKeyElement) {
          mapKeyElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  const handleOpenPropertiesDrawer = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setOpenPropertiesDrawer(true));
  };

  // Drawing ends automatically when a polygon is completed.
  const handleStartPolygonDrawing = () => {
    if (isPolygonDrawingMode) return;
    dispatch(setPolygonDrawingMode(true));

    Analytics.send({
      event: "Start Polygon Drawing",
      SurveyID: survey?.id,
      SurveyName: survey?.name,
    });
  };

  return (
    <section className="flex flex-col gap-y-4 w-full text-sm">
      <div>
        Designate or add a current location by
        <a
          href="#"
          onClick={handleOpenPropertiesDrawer}
          className="text-emerald-700 underline ml-1 mr-1 cursor-pointer"
        >
          editing your properties
        </a>
        and checking &quot;Current Location&quot;.
      </div>
      Create a Market Map using interactive tools
      <div className="flex flex-col">
        <EmeraldCheckbox
          label="Show Header"
          checked={survey?.isIncludeHeader || false}
          onChange={handleCheckboxChange("isIncludeHeader")}
        />
        <div className="flex items-center justify-between">
          <EmeraldCheckbox
            label="Show Legend"
            checked={survey?.isIncludeLegend || false}
            onChange={handleCheckboxChange("isIncludeLegend")}
          />
          <EmeraldIconButton
            icon="edit"
            title="Edit Columns"
            type="Icon"
            onClick={toggleOpenLegendDrawer}
          />
        </div>
        <div className="flex items-center justify-between">
          <EmeraldCheckbox
            label="Show Map Key"
            checked={survey?.isIncludeMapKey || false}
            onChange={handleCheckboxChangeForCompsAndKeys("isIncludeMapKey")}
            id="toggle-map-key-checkbox"
          />
          <EmeraldIconButton
            icon="edit"
            title="Edit Map Key"
            type="Icon"
            onClick={toggleOpenMapKeyDrawer}
            id="edit-map-key-icon"
          />
        </div>
        <div className="flex items-center justify-between">
          <EmeraldCheckbox
            label="Show Points of Interest"
            checked={survey?.isIncludePOI || false}
            onChange={handleCheckboxChangeForCompsAndKeys("isIncludePOI")}
            disabled={!hasVisiblePOIs}
            title={!hasVisiblePOIs ? "No visible POIs available. Unhide at least one POI to enable." : undefined}
            id="toggle-poi-checkbox"
          />
        </div>
        {isUSUser() && (
          <div className="flex items-center justify-between">
            <EmeraldCheckbox
              label="Show Comps"
              checked={survey?.isIncludeLeaseComps || false}
              onChange={handleCheckboxChangeForCompsAndKeys(
                "isIncludeLeaseComps",
              )}
            />
          </div>
        )}
        <EmeraldCheckbox
          label="Marine Ports"
          checked={survey?.isMarinePorts || false}
          onChange={handleCheckboxChange("isMarinePorts")}
        />

        {/* Polygon Section */}
        <hr className="my-2 border-gray-200" />
        <div className="flex items-center justify-between">
          <EmeraldCheckbox
            label="Add Polygons"
            checked={survey?.isIncludePolygons || false}
            onChange={handleCheckboxChange("isIncludePolygons")}
            id="toggle-polygons-checkbox"
          />
        </div>

        {survey?.isIncludePolygons && (
          <div className="pt-1">
            <div className="flex items-center gap-3 mb-2">
              <EmeraldButton
                onClick={handleStartPolygonDrawing}
                variant={
                  isPolygonDrawingMode
                    ? ("Primary" as const)
                    : ("Secondary" as const)
                }
                className="flex-1"
                icon="draw"
                disabled={isPolygonDrawingMode}
              >
                Draw Polygon
              </EmeraldButton>
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {polygonCount} polygon{polygonCount !== 1 ? "s" : ""}
              </span>
            </div>

            {isPolygonDrawingMode && (
              <p className="text-data text-sm leading-5">
                Click on the map to start your polygon, click again to place
                additional corners, then double-click to finish.
              </p>
            )}
          </div>
        )}
      </div>
      <LegendDrawer
        isOpen={isLegendDrawerOpen}
        onClose={toggleOpenLegendDrawer}
      />
      <MarketMapKey
        isOpen={isMapKeyDrawerOpen}
        onClose={toggleOpenMapKeyDrawer}
      />
    </section>
  );
};

export default MarketMapSection;
